import axios from "axios";
import { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCasoData } from "./useCasoData";
import { useTimer } from "./useTimer";
import { API_BASE_URL } from "../utils/constants";
import { formatarDuracao } from "../utils/formatters";

export const useVeiculoOrchestrator = () => {
  const { hash } = useParams();
  const navigate = useNavigate();

  const [modalInfo, setModalInfo] = useState({ show: false });
  const [errorInfo, setErrorInfo] = useState({ show: false, message: "" });

  const displayError = useCallback((message) => {
    setErrorInfo({ show: true, message });
    setTimeout(() => setErrorInfo({ show: false, message: "" }), 4000);
  }, []);

  const { caseData, isAlreadyAnswered, refetchCaseData } = useCasoData(hash, displayError);
  const { timeLeft, isExpired, timerRef } = useTimer(caseData?.confirmado_em);

  const handleConfirm = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/v1/caso/${hash}/visualizar`);
      await refetchCaseData();
    } catch (error) {
      console.error("Falha ao confirmar visualização:", error);
      displayError(
        "Não foi possível confirmar a visualização. Recarregue a página e tente novamente."
      );
    }
  };

  const handleReject = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/v1/caso/${hash}/rejeitar`);
      // Após rejeitar com sucesso, navega para a tela de sucesso genérica
      navigate("/sucesso");
    } catch (error) {
      console.error("Falha ao rejeitar o caso:", error);
      displayError(
        "Não foi possível registrar sua resposta. Recarregue a página e tente novamente."
      );
    }
  };

  const handleSubmit = async (formInput) => {
    if (!formInput) {
      displayError("Por favor, preencha os campos com (*) antes de enviar.");
      return;
    }
    const payload = {
      dados_bem: formInput.dados_bem,
      dados_ficha: formInput.dados_ficha,
      situacao: formInput.situacao?.value || "Não informada",
      info_recompensa: formInput.recompensa,
      avaliacao: {
        criterios: formInput.avaliacao,
        comentario: formInput.comentarioAvaliacao || "",
      },
    };

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/caso/${hash}`,
        payload
      );
      const casoAtualizado = response.data.caso;
      let responseTime = "";
      if (casoAtualizado.confirmado_em && casoAtualizado.respondido_em) {
        const duration =
          new Date(casoAtualizado.respondido_em) -
          new Date(casoAtualizado.confirmado_em);
        responseTime = formatarDuracao(duration);
      }
      const finalEncouragement = isExpired
        ? "O seu tempo de resposta está muito longo. Respostas rápidas motivam os localizadores a trazerem mais resultados."
        : "Respostas rápidas como a sua são essenciais para o sucesso da operação. Continue assim!";
      const timeColorClass = isExpired ? "text-yellow-500" : "text-green-600";
      setModalInfo({
        show: true,
        title: "Excelente Trabalho!",
        encouragement: finalEncouragement,
        responseTime,
        timeColor: timeColorClass,
      });
      clearInterval(timerRef.current);
    } catch (error) {
      const errMsg =
        error.response?.data?.message ||
        "Não foi possível conectar à API. Entre em contato com o suporte Locgram.";
      displayError(errMsg);
    }
  };

  const handleSuccessAction = () => {
    setModalInfo({ show: false });
    navigate(`/sucesso/${hash}`);
  };

  return {
    caseData,
    isAlreadyAnswered,
    timeLeft,
    isExpired,
    timerRef,
    modalInfo,
    errorInfo,
    handleConfirm,
    handleReject,
    handleSubmit,
    handleSuccessAction,
    displayError,
  };
};
